import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, ArrowLeft, FlaskConical, ShieldCheck, FileText, Leaf } from 'lucide-react';
import { COSMETIC_ACTIVES, COSMETIC_CATEGORIES, AVAILABILITY, findActive } from '../../data/cosmeticActives';
import { CdaCallout } from '../../components/CdaCallout';

const Badge = ({ k }) => {
  const a = AVAILABILITY[k] || AVAILABILITY.request;
  return <span className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-semibold ${a.cls}`}>{a.label}</span>;
};

const catLabel = (k) => (COSMETIC_CATEGORIES.find((c) => c.key === k) || {}).label || k;

const Hero = ({ title, subtitle, back }) => (
  <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-14 md:py-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {back ? <Link to={back.to} className="inline-flex items-center gap-1 text-sm text-gray-300 hover:text-white mb-4"><ArrowLeft className="w-4 h-4" /> {back.label}</Link> : null}
      <div className="text-xs font-bold tracking-widest text-orange-400 uppercase mb-2">Personal care · Product line</div>
      <h1 className="text-3xl md:text-5xl font-bold">{title}</h1>
      <p className="mt-3 text-base md:text-lg text-gray-300 max-w-3xl">{subtitle}</p>
    </div>
  </section>
);

const Enquire = ({ slug, inci }) => (
  <Link
    to={`/partner/cosmetic-actives?item=${encodeURIComponent(slug)}`}
    className="inline-flex items-center gap-2 rounded-md bg-orange-600 px-4 py-2 text-sm font-semibold text-white hover:bg-orange-700"
    data-testid={`enquire-${slug}`}
  >
    Enquire about {inci.length > 28 ? 'this active' : inci} <ArrowRight className="w-4 h-4" />
  </Link>
);

/* ------------------------------------------------------------------ list */
export const CosmeticActives = () => {
  const [cat, setCat] = useState('all');
  const [q, setQ] = useState('');
  useEffect(() => { document.title = 'Cosmetic Active Ingredients | LAMKO'; window.scrollTo(0, 0); }, []);

  const items = useMemo(() => {
    const s = q.trim().toLowerCase();
    return COSMETIC_ACTIVES.filter((a) => (cat === 'all' || a.category === cat) &&
      (!s || [a.inci, a.short, a.cas, a.iupac, a.function].join(' ').toLowerCase().includes(s)));
  }, [cat, q]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Hero
        title="Cosmetic Active Ingredients"
        subtitle="UV filters, scalp-care, brightening and preservation actives, supplied by LAMKO through its CRDMO and CDMO partner programme. INCI, CAS, IUPAC and typical specifications below; full TDS, SDS and COA on request."
        back={{ to: '/industries/personal-care', label: 'Personal care' }}
      />

      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-3 gap-4 mb-8">
            {[
              [FlaskConical, 'From grams to tonnes', 'Samples for evaluation, kilogram pilot batches, then regular supply, with the same project manager throughout.'],
              [ShieldCheck, 'Specification you can audit', 'HPLC purity, assay, metals and colour on every lot. COA with each shipment; method details shared under CDA.'],
              [FileText, 'Documentation ready', 'TDS, SDS, COA, allergen and impurity statements. Halal and vegan documentation can be arranged for specific actives.'],
            ].map(([Icon, t, d]) => (
              <div key={t} className="rounded-xl border border-gray-200 bg-white p-5">
                <Icon className="w-6 h-6 text-teal-700" />
                <div className="font-semibold text-gray-900 mt-3">{t}</div>
                <div className="text-sm text-gray-600 mt-1 leading-relaxed">{d}</div>
              </div>
            ))}
          </div>

          <div className="flex flex-col md:flex-row md:items-center gap-3 mb-6">
            <div className="flex flex-wrap gap-2">
              {[{ key: 'all', label: 'All' }, ...COSMETIC_CATEGORIES].map((c) => (
                <button key={c.key} type="button" onClick={() => setCat(c.key)}
                  className={`rounded-full px-3.5 py-1.5 text-sm font-medium border ${cat === c.key ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'}`}>
                  {c.label}
                </button>
              ))}
            </div>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search INCI, CAS or function" className="md:ml-auto w-full md:w-72 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-600" data-testid="actives-search" />
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="actives-grid">
            {items.map((a) => (
              <Link key={a.slug} to={`/products/cosmetic-actives/${a.slug}`} className="group rounded-xl border border-gray-200 bg-white p-5 hover:shadow-md hover:border-teal-300 transition-all flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <div className="text-xs font-bold tracking-widest text-gray-500 uppercase">{catLabel(a.category)}</div>
                  <Badge k={a.availability} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mt-2 group-hover:text-teal-800">{a.inci}</h3>
                <div className="text-xs text-gray-500 mt-0.5">{a.short}</div>
                <p className="text-sm text-gray-700 mt-3 leading-relaxed flex-1">{a.function}</p>
                <dl className="mt-4 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  <dt className="text-gray-500">CAS</dt><dd className="text-gray-900 font-medium">{a.cas}</dd>
                  <dt className="text-gray-500">Formula</dt><dd className="text-gray-900 font-medium">{a.formula}</dd>
                  <dt className="text-gray-500">Form</dt><dd className="text-gray-900 font-medium truncate" title={a.appearance}>{a.appearance}</dd>
                </dl>
                <div className="mt-4 text-sm font-semibold text-teal-700 inline-flex items-center gap-1">Details and specification <ArrowRight className="w-4 h-4" /></div>
              </Link>
            ))}
            {!items.length ? <div className="text-sm text-gray-500 col-span-full">No actives match. Try another term, or <Link to="/partner/cosmetic-actives" className="underline font-semibold text-teal-700">ask us about a custom molecule</Link>.</div> : null}
          </div>

          <div className="mt-10"><CdaCallout from="/products/cosmetic-actives" /></div>

          <div className="mt-10 rounded-xl border border-gray-200 bg-white p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-4">
            <Leaf className="w-8 h-8 text-teal-700 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-gray-900">Need an active that is not listed, or a derivative of one that is?</div>
              <div className="text-sm text-gray-600 mt-1">Route scouting, custom synthesis and scale-up run under one enquiry form for research (CRDMO) and manufacturing (CDMO).</div>
            </div>
            <Link to="/partner/cosmetic-actives" className="inline-flex items-center gap-2 rounded-md bg-teal-700 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-800">Start a cosmetic actives enquiry <ArrowRight className="w-4 h-4" /></Link>
          </div>

          <p className="mt-8 text-xs text-gray-500 leading-relaxed">LAMKO is the supplier of record for this line. The actives are produced by audited partner manufacturers and supplied under LAMKO's specification, lot certificate of analysis and quality agreement; LAMKO does not claim in-house manufacture of these materials. Typical values shown are indicative and may be updated; the lot-specific certificate of analysis governs. Regulatory listings and use-level limits differ by market; confirm the applicable regulation for your product. Trade names of other suppliers are not used; ingredients are identified by INCI name and CAS number.</p>
        </div>
      </section>
    </div>
  );
};

/* ------------------------------------------------------------------ detail */
export const CosmeticActiveDetail = () => {
  const { slug } = useParams();
  const a = findActive(slug);
  useEffect(() => { document.title = a ? `${a.inci} · CAS ${a.cas} | LAMKO` : 'Cosmetic Active | LAMKO'; window.scrollTo(0, 0); }, [a]);

  if (!a) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Hero title="Active not found" subtitle="This ingredient is not in the current line." back={{ to: '/products/cosmetic-actives', label: 'All cosmetic actives' }} />
      </div>
    );
  }
  const related = COSMETIC_ACTIVES.filter((x) => x.category === a.category && x.slug !== a.slug).slice(0, 3);
  const jsonLd = {
    '@context': 'https://schema.org', '@type': 'ChemicalSubstance', name: a.inci, alternateName: a.short,
    identifier: [{ '@type': 'PropertyValue', propertyID: 'CAS', value: a.cas }], description: a.function,
    chemicalComposition: a.formula, provider: { '@type': 'Organization', name: 'LAMKO Co., Ltd.' },
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Hero title={a.inci} subtitle={a.function} back={{ to: '/products/cosmetic-actives', label: 'All cosmetic actives' }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-xl border border-gray-200 bg-white p-6">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Badge k={a.availability} />
                <span className="text-xs font-bold tracking-widest text-gray-500 uppercase">{catLabel(a.category)}</span>
              </div>
              <h2 className="font-semibold text-gray-900 mb-3">Identity</h2>
              <dl className="grid sm:grid-cols-[180px_1fr] gap-y-2 gap-x-4 text-sm">
                <dt className="text-gray-500">INCI name</dt><dd className="text-gray-900 font-medium">{a.inci}</dd>
                <dt className="text-gray-500">Also known as</dt><dd className="text-gray-900">{a.short}</dd>
                <dt className="text-gray-500">IUPAC name</dt><dd className="text-gray-900 break-words">{a.iupac}</dd>
                <dt className="text-gray-500">CAS number</dt><dd className="text-gray-900 font-medium">{a.cas}</dd>
                <dt className="text-gray-500">Molecular formula</dt><dd className="text-gray-900">{a.formula}</dd>
                <dt className="text-gray-500">Molecular weight</dt><dd className="text-gray-900">{a.mw}</dd>
                <dt className="text-gray-500">Appearance</dt><dd className="text-gray-900">{a.appearance}</dd>
              </dl>
            </div>

            <div className="rounded-xl border border-gray-200 bg-white p-6">
              <h2 className="font-semibold text-gray-900 mb-3">Typical specification</h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-gray-500 border-b border-gray-200"><th className="py-2 pr-4 font-medium">Parameter</th><th className="py-2 font-medium">Typical value</th></tr></thead>
                  <tbody>
                    {a.specs.map(([k, v]) => <tr key={k} className="border-b border-gray-100"><td className="py-2 pr-4 text-gray-700">{k}</td><td className="py-2 text-gray-900 font-medium">{v}</td></tr>)}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500 mt-3">Indicative values. The lot certificate of analysis governs. Full TDS and SDS available on request; analytical methods shared under CDA.</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div className="rounded-xl border border-gray-200 bg-white p-6">
                <h2 className="font-semibold text-gray-900 mb-3">Applications</h2>
                <ul className="text-sm text-gray-700 space-y-1.5">{a.applications.map((x) => <li key={x} className="flex gap-2"><span className="text-teal-600">•</span>{x}</li>)}</ul>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-6">
                <h2 className="font-semibold text-gray-900 mb-3">Regulatory note</h2>
                <p className="text-sm text-gray-700 leading-relaxed">{a.regulatory}</p>
              </div>
            </div>
          </div>

          <aside className="space-y-6">
            <div className="rounded-xl border border-teal-200 bg-teal-50 p-6">
              <div className="text-xs font-bold tracking-widest text-teal-700 uppercase">Samples, pilot, supply</div>
              <div className="font-semibold text-gray-900 mt-1">Enquire about {a.inci}</div>
              <p className="text-sm text-gray-700 mt-2">Request samples, a quotation, or development work on this active. One form covers research and manufacturing.</p>
              <div className="mt-4"><Enquire slug={a.slug} inci={a.inci} /></div>
              <p className="text-xs text-gray-600 mt-3">Prefer a CDA first? <Link to={`/cda?from=/products/cosmetic-actives/${a.slug}`} className="font-semibold text-teal-700 underline underline-offset-2">Request it online</Link>.</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-6">
              <div className="font-semibold text-gray-900">Sourcing and quality</div>
              <p className="text-sm text-gray-700 mt-2 leading-relaxed">LAMKO is the supplier of record for this line. The actives are produced by audited partner manufacturers and supplied under LAMKO's specification, lot certificate of analysis and quality agreement; LAMKO does not claim in-house manufacture of these materials.</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-6">
              <div className="font-semibold text-gray-900">Documents on request</div>
              <ul className="text-sm text-gray-700 mt-2 space-y-1">{['Technical data sheet (TDS)', 'Safety data sheet (SDS)', 'Certificate of analysis (COA)', 'Impurity and allergen statements'].map((x) => <li key={x}>· {x}</li>)}</ul>
            </div>
            {related.length ? (
              <div className="rounded-xl border border-gray-200 bg-white p-6">
                <div className="font-semibold text-gray-900 mb-2">Related actives</div>
                <ul className="text-sm space-y-2">{related.map((r) => <li key={r.slug}><Link to={`/products/cosmetic-actives/${r.slug}`} className="text-teal-700 hover:underline">{r.inci}</Link><div className="text-xs text-gray-500">CAS {r.cas}</div></li>)}</ul>
              </div>
            ) : null}
          </aside>
        </div>
      </section>
    </div>
  );
};

export default CosmeticActives;
